import {computed, PipeTransform, signal, Type} from "@angular/core";
import {ConfigButton} from "./config-button";
import {ConfigModelInterface} from "../../interfaces/config-model.interface";

export class ConfigSelectionModel<T> {
    private readonly _selected = signal<Set<T>>(new Set<T>());

    readonly selected = computed(() => Array.from(this._selected()));

    hasValue(): boolean {
        return this._selected().size > 0;
    }

    isSelected(item: T): boolean {
        return this._selected().has(item);
    }

    select(...items: T[]): void {
        this._selected.update((set) => {
            items.forEach((i) => set.add(i));
            return new Set(set);
        });
    }

    toggle(item: T): void {
        this._selected.update((set) => {
            if (set.has(item)) set.delete(item);
            else set.add(item);
            return new Set(set);
        });
    }

    clear(): void {
        this._selected.set(new Set<T>());
    }
}

/**
 * Represents a table model with configurable options for displaying data, columns,
 * and interaction handling such as row selection, button actions, and row click events.
 *
 * @template T The type of data structure that extends ConfigModelInterface.
 */
export class ConfigTableModel<T extends ConfigModelInterface> {
    public settings: {
        header: boolean
        headerAction: boolean
        search: boolean
    };
    public emptyStateMessage?: string;

    constructor(
        public data: T[],
        public displayedColumns: ConfigTableDisplayedColumns[],
        public selectionModel?: ConfigSelectionModel<T>,
        public buttons?: ConfigButton[],
        public onRowClick?: (value: ConfigModelInterface) => Promise<any>,
        public shouldGreyOut?: (value: any) => boolean,
        settings?: Partial<{ header: boolean, headerAction: boolean, search: boolean }>
    ) {
        this.settings = {
            header: settings?.header ?? true,
            headerAction: settings?.headerAction ?? true,
            search: settings?.search ?? true,
        };
    }
}


export class ConfigTableDisplayedColumns {
    public HeaderCellName?: string;
    public PropertyName!: string;
    public HeaderPipes: PipeTransform[] = [];
    public BodyPipes: PipeTransform[] = [];
    public Component: Type<any> | undefined = undefined;

    /**
     * Builds an array of `ConfigTableDisplayedColumns` instances based on the provided column definitions.
     *
     * @param {...{HeaderCellName: string, PropertyName: string, HeaderPipes?: PipeTransform[], BodyPipes?: PipeTransform[], Component?: Type<any>}} columns
     * An array of objects representing column definitions. Each object should contain the following properties:
     * - `HeaderCellName`: A string representing the name of the header cell.
     * - `PropertyName`: A string representing the property name associated with the column.
     * - `HeaderPipes` (optional): An array of `PipeTransform` instances used as pipes for the header cell.
     * - `BodyPipes` (optional): An array of `PipeTransform` instances used as pipes for the body cells.
     * - `Component` (optional): A custom component of type `Type<any>` associated with the column. When using Components you can pass inputs as value. PropertyName: 'inputs'
     *
     * @return {ConfigTableDisplayedColumns[]}
     * An array of `ConfigTableDisplayedColumns` instances created based on the input column definitions.
     */
    static build(...columns: {HeaderCellName: string, PropertyName: string, HeaderPipes?: PipeTransform[], BodyPipes?: PipeTransform[], Component?: Type<any>}[]): ConfigTableDisplayedColumns[] {
        return columns.map(item => {
            const instance = new ConfigTableDisplayedColumns();
            instance.HeaderCellName = item.HeaderCellName;
            instance.PropertyName = item.PropertyName;
            instance.HeaderPipes = item.HeaderPipes || [];
            instance.BodyPipes = item.BodyPipes || [];
            instance.Component = item.Component || undefined;
            return instance;
        });
    }
}
